import type { ProductMasterTechStackSummaries } from './productMasterTechStackSummary';
import type { ProductData } from './productMasterTypes';
import { normalizeProductLifecycleValue } from './productLifecycle';
import { normalizeSurfaceTypes } from './productSurfaceTypes';
import {
  bigintToExportString,
  compareProductCodes,
  exportContactMemberEmails,
  exportRoleMention,
  exportRoleUserEmail,
} from './productMasterExportCore';

function listExportScalarField(raw: unknown, fallback: string): string {
  if (raw === null || raw === undefined) {
    return fallback;
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t || fallback;
  }
  if (typeof raw === 'number') {
    return raw.toString();
  }
  if (typeof raw === 'boolean') {
    return raw ? 'true' : 'false';
  }
  if (typeof raw === 'bigint') {
    return bigintToExportString(raw);
  }
  return fallback;
}

/** Build {@link ProductData} list fields from aggregated detail (same values as Product Master table when sourced from catalog). */
export function listRowFromProductMasterDetails(
  productCode: string,
  detail: Record<string, unknown> | undefined,
): ProductData {
  if (!detail || typeof detail !== 'object') {
    return {
      productCode,
      category: 'Unknown',
      surfaceTypes: [],
      segment: 'Unknown',
      domain: 'Unknown',
      division: 'Unknown',
      lifecycle: 'unknown',
    };
  }
  return {
    productCode: listExportScalarField(detail.productCode, productCode),
    category: listExportScalarField(detail.category, 'Unknown'),
    surfaceTypes: normalizeSurfaceTypes(detail.surfaceTypes),
    segment: listExportScalarField(detail.segment, 'Unknown'),
    domain: listExportScalarField(detail.domain, 'Unknown'),
    division: listExportScalarField(detail.division, 'Unknown'),
    lifecycle: listExportScalarField(detail.lifecycle, 'unknown'),
  };
}

export interface ProductMasterBulkExportItemLike {
  productCode: string;
  productMasterDetails?: Record<string, unknown>;
  error?: string;
  message?: string;
}

/** Fixed role lists on GET /v1/products/{code}. Extra catalog roles are appended, sorted. */
const DEVELOPMENT_ROLES = [
  'lead-qa',
  'lead-designer',
  'lead-pdm',
  'lead-engineer',
  'head-of-development',
  'responsible-pic',
] as const;

const BUSINESS_ROLES = [
  'business-manager',
  'sales-manager',
  'customer-success-manager',
  'customer-support-manager',
] as const;

function metaString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function optionalText(value: unknown): string | null {
  const text = metaString(value);
  return text || null;
}

function detailRecord(
  detail: Record<string, unknown> | undefined,
): Record<string, unknown> {
  return detail && typeof detail === 'object' && !Array.isArray(detail)
    ? detail
    : {};
}

/** Aggregation title-cases role keys (`lead-qa` → `Lead Qa`). Restore the catalog key. */
function catalogRoleKey(label: string): string {
  return label.trim().toLowerCase().replaceAll(/\s+/g, '-');
}

function annotationUrl(value: unknown): string | null {
  const text = optionalText(value);
  if (!text) {
    return null;
  }
  return text.startsWith('url:') ? optionalText(text.slice(4)) : text;
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter(
      (item): item is string =>
        typeof item === 'string' && item.trim().length > 0,
    )
    .map(item => item.trim());
}

function plainObjectOrNull(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function cloudAccount(value: unknown): { id?: string; name?: string } | null {
  const rec = plainObjectOrNull(value);
  if (!rec) {
    return null;
  }
  const id = optionalText(rec.id);
  const name = optionalText(rec.name);
  if (!id && !name) {
    return null;
  }
  return { ...(id ? { id } : {}), ...(name ? { name } : {}) };
}

function coreContactGroup(
  detail: Record<string, unknown>,
  prefix: 'owner' | 'emergency' | 'sre',
): Record<string, unknown> | null {
  const title = optionalText(detail[`${prefix}Title`]);
  const channel = optionalText(detail[`${prefix}Channel`]);
  const channelId = optionalText(detail[`${prefix}ChannelId`]);
  const group = optionalText(detail[`${prefix}Mention`]);
  const members = exportContactMemberEmails(detail[`${prefix}Members`]);
  const catalogUrl = optionalText(detail[`${prefix}CatalogUrl`]);
  if (
    !title &&
    !channel &&
    !channelId &&
    !group &&
    members.length === 0 &&
    !catalogUrl
  ) {
    return null;
  }
  return {
    title,
    slack: { channel, channel_id: channelId, group },
    members,
  };
}

type ExportedRole = {
  role: string;
  user: string | null;
  mention: string | null;
};

function shapeRoles(rows: unknown, order: readonly string[]): ExportedRole[] {
  const stored = new Map<
    string,
    { user: string | null; mention: string | null }
  >();
  if (Array.isArray(rows)) {
    for (const row of rows) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        continue;
      }
      const label = optionalText((row as { role?: unknown }).role);
      if (!label) {
        continue;
      }
      stored.set(catalogRoleKey(label), {
        user: exportRoleUserEmail(row),
        mention: exportRoleMention(row),
      });
    }
  }
  const roleEntry = (role: string): ExportedRole => {
    const value = stored.get(role);
    return {
      role,
      user: value?.user ?? null,
      mention: value?.mention ?? null,
    };
  };
  const known = order.map(roleEntry);
  const extra = [...stored.keys()]
    .filter(role => !order.includes(role))
    .sort((left, right) => left.localeCompare(right))
    .map(roleEntry);
  return [...known, ...extra];
}

function exportLinks(
  raw: unknown,
): Array<{ title: string | null; url: string; icon: string | null }> {
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set<string>();
  const links: Array<{
    title: string | null;
    url: string;
    icon: string | null;
  }> = [];
  for (const item of raw) {
    const rec = plainObjectOrNull(item);
    const url = rec ? optionalText(rec.url) : null;
    if (!rec || !url || seen.has(url)) {
      continue;
    }
    seen.add(url);
    links.push({
      title: optionalText(rec.title),
      url,
      icon: optionalText(rec.icon),
    });
  }
  return links;
}

function nameAndType(item: unknown): { name: string; type: string } | null {
  const rec = plainObjectOrNull(item);
  if (!rec) {
    return null;
  }
  const name = optionalText(rec.name);
  const type = optionalText(rec.type);
  if (!name || !type) {
    return null;
  }
  return { name, type };
}

function nameAndTypeList(raw: unknown): Array<{ name: string; type: string }> {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap(item => {
    const entry = nameAndType(item);
    return entry ? [entry] : [];
  });
}

function namedCategory(
  item: unknown,
): { name: string; category: string } | null {
  const rec = plainObjectOrNull(item);
  if (!rec) {
    return null;
  }
  const name = optionalText(rec.name);
  if (!name) {
    return null;
  }
  return { name, category: optionalText(rec.category) ?? '' };
}

function namedCategoryList(
  raw: unknown,
): Array<{ name: string; category: string }> {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap(item => {
    const entry = namedCategory(item);
    return entry ? [entry] : [];
  });
}

function platformMappings(
  detail: Record<string, unknown>,
  techStack?: ProductMasterTechStackSummaries,
): Record<string, unknown> {
  const mappings = plainObjectOrNull(detail.platformMappings) ?? {};
  const mapped: Record<string, unknown> = {
    product_codes: stringList(mappings.productCodes),
    sonarqube: stringList(mappings.sonarqube),
    k8s_namespaces: stringList(mappings.k8sNamespaces),
    github_frontend: stringList(mappings.githubFrontend),
    github_backend: stringList(mappings.githubBackend),
  };
  if (techStack) {
    mapped.frontend_tech_stack = techStack.frontend;
    mapped.backend_tech_stack = techStack.backend;
  }
  return mapped;
}

function apiShapedProduct(
  item: ProductMasterBulkExportItemLike,
  techStackByProductCode?: Readonly<
    Record<string, ProductMasterTechStackSummaries>
  >,
): Record<string, unknown> {
  if (item.error) {
    const failed: Record<string, unknown> = {
      product_code: item.productCode,
      error: item.error,
    };
    if (item.message) {
      failed.message = item.message;
    }
    return failed;
  }

  const detail = detailRecord(item.productMasterDetails);
  const lifecycle = metaString(detail.lifecycle);
  return {
    metadata: {
      product_code: metaString(detail.productCode) || item.productCode,
      product_id: metaString(detail.productId),
      description:
        metaString(detail.description) || metaString(detail.productDescription),
      name_en: metaString(detail.productNameEn),
      name_jp: metaString(detail.productNameJp),
      category: metaString(detail.category),
      lifecycle: lifecycle ? normalizeProductLifecycleValue(lifecycle) : '',
      segment: metaString(detail.segment),
      domain: metaString(detail.domain),
      division: metaString(detail.division),
    },
    spec: {
      contacts: {
        core: {
          owner: coreContactGroup(detail, 'owner'),
          emergency: coreContactGroup(detail, 'emergency'),
          sre: coreContactGroup(detail, 'sre'),
        },
        development_roles: shapeRoles(detail.devContacts, DEVELOPMENT_ROLES),
        business_roles: shapeRoles(detail.bizContacts, BUSINESS_ROLES),
      },
      documentation: {
        techdocs_url: annotationUrl(detail.techDocsUrl),
      },
      links: exportLinks(detail.links),
      platform_mappings: platformMappings(
        detail,
        techStackByProductCode
          ? techStackByProductCode[item.productCode] ?? {
              frontend: '',
              backend: '',
            }
          : undefined,
      ),
      infrastructure: {
        environment: optionalText(detail.environment),
        aws: {
          production: cloudAccount(detail.awsProdAccount),
          staging: cloudAccount(detail.awsStagingAccount),
        },
        azure: {
          production: cloudAccount(detail.azureProdSubscription),
          staging: cloudAccount(detail.azureStagingSubscription),
        },
        admin_urls: {
          production: optionalText(detail.adminWebProdUrl),
          staging: optionalText(detail.adminWebStagingUrl),
          workflow: optionalText(detail.awebWorkflowUrl),
        },
      },
      governance: {
        regulations: plainObjectOrNull(detail.regulations),
        jiima_certification: plainObjectOrNull(detail.jiimaCertification),
      },
      surface_type: normalizeSurfaceTypes(detail.surfaceTypes),
      k8s_services: nameAndTypeList(detail.services),
      relations: {
        dependencies: {
          dependsOn: namedCategoryList(detail.dependsOnProducts),
          dependencyOf: namedCategoryList(detail.dependencyOfProducts),
        },
        apis: {
          provides: nameAndTypeList(detail.providesApis),
          consumes: nameAndTypeList(detail.consumesApis),
        },
        resources: nameAndTypeList(detail.resources),
      },
    },
    created_at: optionalText(detail.createdAt),
    updated_at: optionalText(detail.updatedAt),
  };
}

/**
 * Product Master JSON file. Each product matches GET /v1/products/{code} `data`.
 * `created_at` / `updated_at` are the raw product.yaml annotation timestamps (null when
 * unset); midnight UTC on the 1st in `created_at` means "that month or earlier".
 * CSV stays on the flat column export.
 */
export function buildProductMasterJsonFileExportDocument(
  items: ProductMasterBulkExportItemLike[],
  techStackByProductCode?: Readonly<
    Record<string, ProductMasterTechStackSummaries>
  >,
): {
  exported_at: string;
  product_count: number;
  products: Record<string, unknown>[];
} {
  const products = [...items]
    .sort((a, b) => compareProductCodes(a.productCode, b.productCode))
    .map(item => apiShapedProduct(item, techStackByProductCode));

  return {
    exported_at: new Date().toISOString(),
    product_count: products.length,
    products,
  };
}
